import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";

import { pageCount } from "@/core/api/pagination";
import { DataTableClar } from "@/core/components/shared/data-table";
import type { ApprovalRequestFilter } from "@/features/admin/api/dto";
import { useApprovalRequests } from "@/features/admin/api/use-approval-requests";
import {
  approvalRequestsColumns,
  type ApprovalRequestRow,
} from "@/features/admin/components";
import { useAdminTranslation } from "@/features/admin/i18n/use-admin-translation";

/** Traduit les filtres de colonnes DataTableClar en `ApprovalRequestFilter` (§4.2). */
const toApprovalRequestFilter = (
  filters: ColumnFiltersState
): ApprovalRequestFilter => {
  const filter: ApprovalRequestFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "actionType")
      filter.actionType = value as ApprovalRequestFilter["actionType"];
    if (id === "status")
      filter.status = value as ApprovalRequestFilter["status"];
    if (id === "sourceService") filter.sourceService = value;
  }
  return filter;
};

/**
 * File d'attente des demandes d'approbation maker-checker
 * (`POST /ApprovalRequests/all`, guide §6.8). Rôles : flashpay-superadmin,
 * flashpay-admin, flashpay-service (garde de route dans le router).
 */
export function ApprovalRequestsPage() {
  const { t } = useAdminTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toApprovalRequestFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useApprovalRequests(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("admin.approvals.title")}
        </h1>
        <p className="max-w-2xl text-sm text-muted-foreground">
          {t("admin.approvals.subtitle")}
        </p>
      </div>

      <DataTableClar
        data={(data?.items ?? []).filter((r): r is ApprovalRequestRow =>
          Boolean(r.id)
        )}
        columnsClar={approvalRequestsColumns}
        title={t("admin.approvals.title")}
        exportFileName="approval-requests"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />
    </section>
  );
}
